import Mustache from 'mustache';
import analytics from './../analytics';

/**
 * @type {int} how long are waited before search is performed
 */
const SEARCH_TIMEOUT = 500;//ms

const AMOUNT_OF_ITEMS_SHOWN = 16;

/**
 * Selectors used by site search
 */
const dom = {
  searchButton: 'header .f-icon--search, header button.search',
  searchInput: 'input[name="search"]',
  searchElement: '.site-search__search-element',
  searchResultElement: '.site-search__search-results',
  template: '.search-result-mustache-template',
  closeButton: '.site-search__close-button'
};

/**
 * Site search is handled by this class. Site search is made to Stage when a user presses and releases button.
 * This sends a HTTP request to server side and  response is handled. Search results are rendered by using Mustache template.
 */
class SiteSearch {

  /**
   * @param {Element} el HTML element to bind to
   */
  constructor( el ) {
    this._el = el;
    this.dom = dom;
    this._bind();
  }

  /**
   * Asynchronous search function. Makes a ajax request to the server site search endpoint.
   * @param {string} value search text
   * @return {object} server response object
   * @async
   */
  async search( value ) {
    const baseUrl = window.location.origin;
    const response = await fetch( baseUrl + `/stage-ajax/v2-site-search?search=${value}`);
    return response.json();
  }

  /**
   * Binds events listeners to document object model
   */
  _bind() {
    this._el.querySelector( this.dom.searchInput ).addEventListener('keyup', this._onSearchKeyup.bind(this) );
    document.querySelectorAll( this.dom.searchButton ).forEach( element => {
      element.addEventListener('click', this._onSearchButtonClicked.bind(this) );
    });
    this._el.querySelector( this.dom.closeButton ).addEventListener('click', this._onCloseButtonClicked.bind(this) );

    //Due bind functions creates a new function. We need to store the reference to new function somewhere to
    //remove event listener from the window object when site search is closed.
    this._onWindowClicked = this._onWindowClicked.bind(this)
  }

  /**
   * Closes site search if document is clicked outside of .site-search component.
   * @param {Event} event event object
   */
  _onWindowClicked( event ) {
    if ( document.body.classList.contains('site-search--open') && !event.target.closest(dom.searchElement)) {
      this._closeSiteSearch();
    }
  }

  /**
   * Called when search button is clicked. Opens the search menu.
   * @param {event} event mouse click event object
   */
  _onSearchButtonClicked( event ) {
    event.preventDefault();
    event.stopImmediatePropagation();//Prevent events ending to

    window.addEventListener('click', this._onWindowClicked );

    document.body.classList.remove('site-search--close');
    document.body.classList.add('site-search--open');

    this._el.querySelector( this.dom.searchElement ).classList.toggle('site-search__search-element--visible', true);
    this._el.querySelector( this.dom.searchInput ).focus();
    analytics.pushUIInteractionAnalytics({
      element: 'link', elementName: 'UI - Haku - Avaa', type: 'click'
    });
  }

  /**
   * Close site search component.
   */
  _closeSiteSearch() {
    this._el.querySelector( this.dom.searchInput ).value = '';

    document.body.classList.remove('site-search--open');
    document.body.classList.add('site-search--close');

    this._el.querySelector( this.dom.searchElement ).classList.remove('site-search__search-element--visible');
    this._el.querySelector( this.dom.searchResultElement ).classList.remove( this.dom.searchResultElement.replace('.','') + '--open')
    this._el.querySelector( this.dom.searchResultElement ).innerHTML = '';
    document.body.classList.remove('noscroll-mobile');
    this._loadingEnds();

    window.removeEventListener('click', this._onWindowClicked )
  }

  /**
   * Called when a close button is clicked. Closes menu and empties previous search result.
   */
  _onCloseButtonClicked() {
    this._closeSiteSearch();
  }

  /**
   * Called when a button is pressed and released. Search is started after the search text is least 3 letters long.
   * @param {event} event key pressed event object
   */
  _onSearchKeyup( event ) {

    this._loadingEnds();

    if( this._searchTimeout ) {
      clearTimeout(this._searchTimeout);
    }

    const searchValue = event.currentTarget.value;

    if (searchValue.length > 1) {

      this._searchTimeout = setTimeout( async () => {
        try {
          this._loadingStart();
          const responseJSON = await this.search( searchValue );

          analytics.pushUIInteractionAnalytics({
            element: 'input',
            elementName: 'UI - Haku - Hakusanan kirjoitus',
            information: searchValue,
            type: 'click'
          }, 'UI Interaction String');

          if( responseJSON.status ) {
            this._showResponse( searchValue, responseJSON );
          } else {
            this._showError( responseJSON );
          }

        } catch( error ) {
          this._showError( error );
        } finally {
          this._loadingEnds();
        }
      }, SEARCH_TIMEOUT)
    }
  }

  /**
   * Shows search result after server reponst to the search request.
   * @param {string} searchValue user's typed search string
   * @param {object} response search request JSON object
   */
  _showResponse( searchValue, response ) {

    document.body.classList.add('noscroll-mobile');

    this._el.querySelector( this.dom.searchResultElement ).classList.add( this.dom.searchResultElement.replace('.','') + '--open')

    const re = new RegExp('(' + searchValue + ')', 'gi');

    let results = response.results.map( result => {
      result.match = result.title.replace(re, '<span class="caption">$1</span>')
      return result;
    });

    const totalAmount = results.length;
    results = results.slice(0, AMOUNT_OF_ITEMS_SHOWN);

    const template = this._el.querySelector( this.dom.template ).text;
    const markdown = Mustache.render( template, { results: results, totalAmount, noresults: totalAmount === 0, loading: false } )
    this._el.querySelector( this.dom.searchResultElement ).innerHTML = markdown;
  }

  _loadingStart() {
    this._el.querySelector( this.dom.searchResultElement ).classList.add( this.dom.searchResultElement.replace('.','') + '--open');
    this._el.querySelector( this.dom.searchResultElement ).classList.add( this.dom.searchResultElement.replace('.','') + '--loading');
    const template = this._el.querySelector( this.dom.template ).text;
    const markdown = Mustache.render( template, { results: [], totalAmount: 0, noresults: false, loading: true } )
    this._el.querySelector( this.dom.searchResultElement ).innerHTML = markdown;
  }

  _loadingEnds() {
    this._el.querySelector( this.dom.searchResultElement ).classList.remove( this.dom.searchResultElement.replace('.','') + '--loading');
  }

  /**
   * Called when error occurs
   * @param {object} error error object
   */
  _showError( error ) {
    console.log( error );
  }

}

export default SiteSearch
export { SiteSearch }
